/**
 * Frontend Monitoring Service
 *
 * This service provides client-side monitoring capabilities including:
 * - Performance metrics collection
 * - Error tracking
 * - User interaction tracking
 * - API call monitoring
 *
 * For production, this would typically integrate with a service like New Relic,
 * Datadog, or AWS CloudWatch RUM (Real User Monitoring).
 */

class MonitoringService {
  constructor() {
    this.enabled = process.env.REACT_APP_MONITORING_ENABLED === 'true';
    this.environment = process.env.REACT_APP_ENV || 'development';
    this.applicationVersion = process.env.REACT_APP_VERSION || '0.0.0';
    this.sessionId = this.generateSessionId();
    this.queue = [];
    this.flushInterval = null;

    // Initialize error tracking
    if (this.enabled) {
      this.initErrorTracking();
      this.initPerformanceTracking();
      this.startFlushInterval();

      // Log initialization
      console.info('[Monitoring] Service initialized', {
        environment: this.environment,
        version: this.applicationVersion,
        sessionId: this.sessionId,
      });
    }
  }

  /**
   * Generate a unique session ID
   */
  generateSessionId() {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  }

  /**
   * Initialize global error tracking
   */
  initErrorTracking() {
    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.trackError('unhandledRejection', event.reason);
    });

    // Capture global errors
    window.addEventListener('error', (event) => {
      this.trackError('uncaughtError', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error ? event.error.stack : null,
      });
    });
  }

  /**
   * Initialize performance tracking
   */
  initPerformanceTracking() {
    // Track page load performance
    if (window.performance && window.performance.timing) {
      window.addEventListener('load', () => {
        // Wait for page to finish loading
        setTimeout(() => {
          const timing = window.performance.timing;
          const performanceMetrics = {
            dnsLookup: timing.domainLookupEnd - timing.domainLookupStart,
            tcpConnection: timing.connectEnd - timing.connectStart,
            serverResponse: timing.responseStart - timing.requestStart,
            domProcessing: timing.domComplete - timing.responseEnd,
            totalPageLoad: timing.loadEventEnd - timing.navigationStart,
          };

          this.trackPerformance('pageLoad', performanceMetrics);
        }, 0);
      });
    }

    // Track Core Web Vitals if supported
    if ('web-vitals' in window) {
      import('web-vitals').then(({ getCLS, getFID, getLCP, getFCP, getTTFB }) => {
        getCLS(metric => this.trackPerformance('CLS', metric));
        getFID(metric => this.trackPerformance('FID', metric));
        getLCP(metric => this.trackPerformance('LCP', metric));
        getFCP(metric => this.trackPerformance('FCP', metric));
        getTTFB(metric => this.trackPerformance('TTFB', metric));
      });
    }
  }

  /**
   * Start the queue flush interval
   */
  startFlushInterval() {
    // Flush metrics every 10 seconds
    this.flushInterval = setInterval(() => {
      this.flushQueue();
    }, 10000);
  }

  /**
   * Track an error
   * @param {string} errorType - Type of error
   * @param {Error|Object|string} error - Error object or message
   * @param {Object} metadata - Additional metadata
   */
  trackError(errorType, error, metadata = {}) {
    if (!this.enabled) return;

    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorStack = error instanceof Error ? error.stack : null;

    const errorData = {
      type: 'error',
      errorType,
      message: errorMessage,
      stack: errorStack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      metadata: {
        userAgent: navigator.userAgent,
        ...metadata,
      },
    };

    this.queue.push(errorData);

    // Also log to console in dev environment
    if (this.environment !== 'production') {
      console.error('[Monitoring] Error tracked:', errorData);
    }

    // If queue is getting large, flush immediately
    if (this.queue.length >= 10) {
      this.flushQueue();
    }
  }

  /**
   * Track a performance metric
   * @param {string} metricName - Name of the metric
   * @param {Object} data - Metric data
   */
  trackPerformance(metricName, data) {
    if (!this.enabled) return;

    const metricData = {
      type: 'performance',
      metricName,
      data,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    };

    this.queue.push(metricData);

    // Log in development
    if (this.environment !== 'production') {
      console.info(`[Monitoring] Performance metric tracked: ${metricName}`, data);
    }
  }

  /**
   * Track a user action/event
   * @param {string} eventName - Name of the event
   * @param {Object} data - Event data
   */
  trackEvent(eventName, data = {}) {
    if (!this.enabled) return;

    const eventData = {
      type: 'event',
      eventName,
      data,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    };

    this.queue.push(eventData);
  }

  /**
   * Track API call performance
   * @param {string} endpoint - API endpoint
   * @param {string} method - HTTP method
   * @param {number} duration - Call duration in ms
   * @param {number} status - HTTP status code
   * @param {boolean} isError - Whether the call resulted in an error
   */
  trackApiCall(endpoint, method, duration, status, isError = false) {
    if (!this.enabled) return;

    const apiData = {
      type: 'api',
      endpoint,
      method,
      duration,
      status,
      isError,
      timestamp: new Date().toISOString(),
    };

    this.queue.push(apiData);

    // Log slow API calls
    if (duration > 1000 && this.environment !== 'production') {
      console.warn(`[Monitoring] Slow API call: ${method} ${endpoint} (${duration}ms)`);
    }
  }

  /**
   * Flush the queue by sending data to backend
   */
  flushQueue() {
    if (!this.enabled || this.queue.length === 0) return;

    const metrics = [...this.queue];
    this.queue = [];

    // For production, this would send to a backend endpoint
    // In this implementation, we'll just log it
    if (this.environment === 'production') {
      // In a real implementation, this would send to a backend API
      // fetch('/api/metrics', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({
      //     sessionId: this.sessionId,
      //     applicationVersion: this.applicationVersion,
      //     environment: this.environment,
      //     metrics
      //   })
      // }).catch(err => console.error('Failed to send metrics:', err));

      // For now, just log to console
      console.debug(`[Monitoring] Flushed ${metrics.length} metrics`);
    } else {
      console.debug(`[Monitoring] Would have sent ${metrics.length} metrics in production`);
    }
  }

  /**
   * Clean up resources when component unmounts
   */
  cleanup() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
    }

    // Flush any remaining metrics
    this.flushQueue();
  }
}

// Create singleton instance
const monitoringService = new MonitoringService();

export default monitoringService;
